import React from 'react';
import { Link } from 'react-router-dom';

interface System {
  slug: string;
  title: string;
  description: string;
  industries: string[];
  compliance: string[];
  icon: React.ReactNode;
}

const systems: System[] = [
  {
    slug: 'ops',
    title: 'Zwift Ops',
    description: 'Find root cause in minutes, not hours, and fix it before customers notice.',
    industries: ['Telecom', 'Utilities', 'Payments', 'Logistics'],
    compliance: ['GDPR', 'NIS2', 'EU AI Act'],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
      </svg>
    ),
  },
  {
    slug: 'advise',
    title: 'Zwift Advise',
    description: 'Customer-facing AI that answers like your best adviser and never says what compliance would not allow.',
    industries: ['Fintech', 'Banking', 'Insurance', 'Wealth'],
    compliance: ['GDPR', 'SOC 2', 'EU AI Act'],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  },
  {
    slug: 'voice',
    title: 'Zwift Voice',
    description: 'An AI receptionist that answers every call, books appointments and protects patient data.',
    industries: ['Medical', 'Dental', 'Physio', 'Property'],
    compliance: ['HIPAA', 'GDPR'],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
  },
  {
    slug: 'hire',
    title: 'Zwift Hire',
    description: 'Screen, shortlist and engage candidates in hours, without bias or data you cannot defend.',
    industries: ['Staffing', 'Healthcare', 'Logistics', 'Enterprise HR'],
    compliance: ['GDPR', 'EU AI Act'],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    slug: 'docs',
    title: 'Zwift Docs',
    description: 'Collect, check and file the documents you chase every week, without the email back-and-forth.',
    industries: ['Construction', 'Real estate', 'Financial services', 'HR'],
    compliance: ['GDPR', 'AML/KYC'],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
];

const Services: React.FC = () => {
  return (
    <section id="systems" className="section-padding bg-neutral-50">
      <div className="container-max">
        <div className="max-w-2xl mb-16">
          <h2 className="text-4xl md:text-5xl font-semibold text-black mb-5 leading-[1.05] tracking-[-0.02em]">
            What we build
          </h2>
          <p className="text-lg text-neutral-600 leading-relaxed">
            Five production-ready systems for regulated operations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {systems.map((system) => (
            <Link
              key={system.slug}
              to={`/systems/${system.slug}`}
              className="bg-white rounded-2xl p-8 border border-neutral-200 flex flex-col h-full hover:border-neutral-400 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center text-neutral-700 mb-5 group-hover:bg-neutral-200 transition-colors">
                {system.icon}
              </div>
              <h3 className="text-lg font-semibold text-black mb-3 tracking-tight">
                {system.title}
              </h3>
              <p className="text-neutral-600 leading-relaxed text-[0.95rem] flex-1">
                {system.description}
              </p>
              <div className="mt-5 flex flex-wrap gap-1.5">
                {system.industries.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-600 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {system.compliance.map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-white border border-neutral-200 text-neutral-500 text-xs"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>

        <p className="mt-10 text-sm text-neutral-500">
          Built on the leading AI stack, deployed in your cloud.
        </p>
      </div>
    </section>
  );
};

export default Services;
